(() => {
  const NAME_COLOR = '#253B7B';
  const CITY_COLOR = '#C12026';
  const FONT_STACK = 'Archivo, "Helvetica Neue", Arial, sans-serif';

  const normalize = (value) => (value || '').trim().toLowerCase();

  const getLastName = (name) => {
    const parts = normalize(name).split(/\s+/).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  };

  const sortMembersByLastName = (classCard) => {
    const list = classCard.querySelector('.hor-class__members');
    if (!list) return;

    const members = Array.from(list.querySelectorAll('[data-hor-member]'));
    members.sort((a, b) => {
      const lastNameCompare = getLastName(a.dataset.name).localeCompare(
        getLastName(b.dataset.name)
      );
      if (lastNameCompare !== 0) return lastNameCompare;
      return normalize(a.dataset.name).localeCompare(normalize(b.dataset.name));
    });

    members.forEach((member) => list.appendChild(member));
  };

  /**
   * Paint label text onto a PNG. Samsung force-dark remaps CSS/SVG text colors
   * but only gently tones raster images, so navy/red stay readable on the plaque.
   */
  const paintLabel = ({ text, color, fontWeight, fontSizePx, showAsterisk }) => {
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const font = `${fontWeight} ${fontSizePx}px ${FONT_STACK}`;
    ctx.font = font;
    const textWidth = ctx.measureText(text).width;

    let asteriskGap = 0;
    let asteriskSize = fontSizePx * 0.75;
    let asteriskWidth = 0;
    if (showAsterisk) {
      asteriskGap = fontSizePx * 0.12;
      ctx.font = `${fontWeight} ${asteriskSize}px ${FONT_STACK}`;
      asteriskWidth = ctx.measureText('*').width;
    }

    const padX = 2;
    const padY = 1;
    const width = Math.max(
      1,
      Math.ceil(textWidth + asteriskGap + asteriskWidth + padX * 2)
    );
    const height = Math.max(1, Math.ceil(fontSizePx * 1.15 + padY * 2));

    canvas.width = Math.ceil(width * dpr);
    canvas.height = Math.ceil(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillStyle = color;

    const midY = height / 2;
    ctx.font = font;
    ctx.fillText(text, padX, midY);

    if (showAsterisk) {
      ctx.font = `${fontWeight} ${asteriskSize}px ${FONT_STACK}`;
      ctx.fillText(
        '*',
        padX + textWidth + asteriskGap,
        midY - fontSizePx * 0.28
      );
    }

    return {
      src: canvas.toDataURL('image/png'),
      width,
      height,
    };
  };

  const applyPaintedLabel = (img, painted) => {
    if (!img || !painted) return false;
    img.src = painted.src;
    img.width = painted.width;
    img.height = painted.height;
    img.style.width = `${painted.width}px`;
    img.style.height = `${painted.height}px`;
    img.classList.add('is-painted');
    return true;
  };

  const paintMemberLabels = (section) => {
    const members = section.querySelectorAll('[data-hor-member]');

    members.forEach((member) => {
      const baseSize = parseFloat(getComputedStyle(member).fontSize) || 14;
      const name = member.dataset.name || '';
      const city = member.dataset.city || '';
      const deceased = member.dataset.deceased === 'true';
      let painted = false;

      const nameImg = member.querySelector('[data-hor-text="name"]');
      if (nameImg && name) {
        painted =
          applyPaintedLabel(
            nameImg,
            paintLabel({
              text: name,
              color: NAME_COLOR,
              fontWeight: '700',
              fontSizePx: baseSize * 0.9,
              showAsterisk: deceased,
            })
          ) || painted;
      }

      const cityImg = member.querySelector('[data-hor-text="city"]');
      if (cityImg && city) {
        painted =
          applyPaintedLabel(
            cityImg,
            paintLabel({
              text: city,
              color: CITY_COLOR,
              fontWeight: '500',
              fontSizePx: baseSize * 0.8,
              showAsterisk: false,
            })
          ) || painted;
      }

      member.classList.toggle('is-labels-painted', painted);
    });
  };

  const schedulePaint = (section) => {
    const run = () => paintMemberLabels(section);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(run).catch(run);
    } else {
      run();
    }
  };

  const isSvgUrl = (url) => /\.svg(?:\?|#|$)/i.test(url || '');

  /**
   * SVG-as-<img> keeps preserveAspectRatio="meet", so CSS box growth does
   * not stretch the artwork. Inline it and set none so extra height distorts.
   */
  const inlinePlaqueSvgs = (section) => {
    const images = Array.from(
      section.querySelectorAll('img.hor-class__image[data-hor-plaque]')
    );

    return Promise.all(
      images.map(async (img) => {
        const src = img.currentSrc || img.getAttribute('src') || '';
        if (!isSvgUrl(src)) return;

        try {
          const response = await fetch(src, {
            mode: 'cors',
            credentials: 'omit',
          });
          if (!response.ok) return;

          const markup = await response.text();
          const parsed = new DOMParser().parseFromString(
            markup,
            'image/svg+xml'
          );
          const svg = parsed.documentElement;
          if (
            !svg ||
            svg.nodeName.toLowerCase() !== 'svg' ||
            parsed.querySelector('parsererror')
          ) {
            return;
          }

          svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          svg.removeAttribute('width');
          svg.removeAttribute('height');
          svg.setAttribute('class', img.className);
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
          svg.setAttribute('data-hor-plaque', '');
          img.replaceWith(svg);
        } catch (error) {
          /* Keep the img; CSS 9-slice fallback still applies. */
        }
      })
    );
  };

  const MOBILE_JERSEY_QUERY = '(max-width: 920px)';

  const unwrapJerseyStacks = (root) => {
    if (!root) return;
    root.querySelectorAll('.hor-class__jersey-stack').forEach((stack) => {
      const source =
        stack.querySelector('.hor-class__jersey-cap--mid .hor-class__image') ||
        stack.querySelector('.hor-class__image');
      if (!source) {
        stack.remove();
        return;
      }
      const restored = source.cloneNode(true);
      restored.classList.remove(
        'hor-class__image--top',
        'hor-class__image--mid',
        'hor-class__image--bot'
      );
      stack.replaceWith(restored);
    });
  };

  const wrapJerseyStack = (art) => {
    if (!art || art.closest('.hor-class__jersey-stack')) return;

    const stack = document.createElement('div');
    stack.className = 'hor-class__jersey-stack';
    stack.setAttribute('aria-hidden', 'true');

    ['top', 'mid', 'bot'].forEach((slot) => {
      const wrap = document.createElement('div');
      wrap.className = `hor-class__jersey-cap hor-class__jersey-cap--${slot}`;
      const clone = art.cloneNode(true);
      clone.classList.add(`hor-class__image--${slot}`);
      wrap.appendChild(clone);
      stack.appendChild(wrap);
    });

    art.replaceWith(stack);
  };

  const wrapPlaqueIfNeeded = (plaque) => {
    if (!plaque || plaque.querySelector('.hor-class__jersey-stack')) return;
    const art = plaque.querySelector(
      ':scope > .hor-class__image[data-hor-plaque], :scope > svg.hor-class__image, :scope > img.hor-class__image--fill'
    );
    if (!art || art.classList.contains('hor-class__image--placeholder')) {
      return;
    }
    wrapJerseyStack(art);
  };

  /**
   * 1-across extra-name and 3-across tall-row jerseys: keep SVG top/bottom
   * unscaled and only lengthen the middle band.
   */
  const DESKTOP_3_QUERY = '(min-width: 1320px)';

  const syncJerseySlices = (section) => {
    const isMobile = window.matchMedia(MOBILE_JERSEY_QUERY).matches;
    const isDesktop3 = window.matchMedia(DESKTOP_3_QUERY).matches;

    if (isMobile) {
      section
        .querySelectorAll('.hor-class:not(.hor-class--extra-names)')
        .forEach((card) => unwrapJerseyStacks(card));
      section
        .querySelectorAll('.hor-class--extra-names .hor-class__plaque')
        .forEach(wrapPlaqueIfNeeded);
      return;
    }

    if (isDesktop3) {
      section
        .querySelectorAll('.hor-class:not(.hor-class--row-tall)')
        .forEach((card) => unwrapJerseyStacks(card));
      section
        .querySelectorAll('.hor-class--row-tall .hor-class__plaque')
        .forEach(wrapPlaqueIfNeeded);
      return;
    }

    unwrapJerseyStacks(section);
  };

  /**
   * Desktop extra-name year nudge from 11/12/13 samples:
   * 11 → -26px, 12 → -11px, 13 → -9px, then +2px per additional name.
   */
  const yearNudgeForExtra = (extra) => {
    if (extra <= 0) return 0;
    if (extra === 1) return -26;
    if (extra === 2) return -11;
    return -9 + (extra - 3) * 2;
  };

  /** Desktop 3-across: only rows with an 11+ class grow; siblings match tallest. */
  const syncTallRows = (section) => {
    const cards = Array.from(
      section.querySelectorAll('[data-hor-class]')
    );

    cards.forEach((card) => {
      card.classList.remove('hor-class--row-tall');
      const plaque = card.querySelector('.hor-class__plaque');
      if (plaque) {
        plaque.style.removeProperty('--hor-row-extra');
        plaque.style.removeProperty('--hor-year-nudge');
      }
    });

    if (window.matchMedia('(max-width: 1319px)').matches) return;

    const visible = cards.filter(
      (card) => !card.classList.contains('hor-class--hidden')
    );
    const rows = [];

    visible.forEach((card) => {
      const top = card.offsetTop;
      let row = rows.find((entry) => Math.abs(entry.top - top) < 4);
      if (!row) {
        row = { top: top, cards: [] };
        rows.push(row);
      }
      row.cards.push(card);
    });

    rows.forEach((row) => {
      let maxExtra = 0;
      row.cards.forEach((card) => {
        const count = parseInt(card.dataset.horMemberCount || '0', 10);
        const extra = Math.max(0, count - 10);
        if (extra > maxExtra) maxExtra = extra;
      });

      if (maxExtra <= 0) return;

      const yearNudge = yearNudgeForExtra(maxExtra);
      row.cards.forEach((card) => {
        card.classList.add('hor-class--row-tall');
        const plaque = card.querySelector('.hor-class__plaque');
        if (plaque) {
          plaque.style.setProperty('--hor-row-extra', String(maxExtra));
          plaque.style.setProperty('--hor-year-nudge', `${yearNudge}px`);
        }
      });
    });
  };

  const initHallOfRecognition = (root) => {
    const sections = root
      ? [root]
      : Array.from(
          document.querySelectorAll('.hor-classes:not([data-hor-initialized])')
        );

    sections.forEach((section) => {
      const filterInput = section.querySelector('[data-hor-filter]');
      const statusEl = section.querySelector('[data-hor-filter-status]');
      const classCards = Array.from(section.querySelectorAll('[data-hor-class]'));

      classCards.forEach((classCard) => sortMembersByLastName(classCard));
      schedulePaint(section);
      syncJerseySlices(section);
      window.requestAnimationFrame(() => {
        syncTallRows(section);
        syncJerseySlices(section);
        window.requestAnimationFrame(() => {
          syncTallRows(section);
          syncJerseySlices(section);
        });
      });
      inlinePlaqueSvgs(section).then(() => {
        schedulePaint(section);
        window.requestAnimationFrame(() => {
          syncTallRows(section);
          syncJerseySlices(section);
          window.requestAnimationFrame(() => {
            syncTallRows(section);
            syncJerseySlices(section);
          });
        });
      });

      if (typeof ResizeObserver !== 'undefined') {
        let resizeTimer = 0;
        const observer = new ResizeObserver(() => {
          window.clearTimeout(resizeTimer);
          resizeTimer = window.setTimeout(() => {
            syncTallRows(section);
            syncJerseySlices(section);
            paintMemberLabels(section);
          }, 120);
        });
        observer.observe(section);
        section._horResizeObserver = observer;
      }

      section.dataset.horInitialized = 'true';

      if (!filterInput || !classCards.length) {
        return;
      }

      const applyFilter = () => {
        const query = normalize(filterInput.value);
        let visibleClassCount = 0;
        let matchCount = 0;

        classCards.forEach((classCard) => {
          const members = Array.from(
            classCard.querySelectorAll('[data-hor-member]')
          );
          let classHasMatch = false;

          members.forEach((member) => {
            const name = normalize(member.dataset.name);
            const city = normalize(member.dataset.city);
            const isMatch =
              !query || name.includes(query) || city.includes(query);

            member.classList.toggle(
              'hor-class__member--match',
              Boolean(query && isMatch)
            );

            if (isMatch) {
              classHasMatch = true;
              if (query) matchCount += 1;
            }
          });

          const hideClass = Boolean(query && !classHasMatch);
          classCard.classList.toggle('hor-class--hidden', hideClass);

          if (!hideClass) {
            visibleClassCount += 1;
          }
        });

        if (statusEl) {
          if (!query) {
            statusEl.hidden = true;
            statusEl.textContent = '';
          } else {
            statusEl.hidden = false;
            statusEl.textContent =
              matchCount === 0
                ? 'No members match your search.'
                : `${matchCount} member${matchCount === 1 ? '' : 's'} found in ${visibleClassCount} class${visibleClassCount === 1 ? '' : 'es'}.`;
          }
        }

        window.requestAnimationFrame(() => {
          syncTallRows(section);
          syncJerseySlices(section);
        });
      };

      filterInput.addEventListener('input', applyFilter);
      filterInput.addEventListener('search', applyFilter);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHallOfRecognition());
  } else {
    initHallOfRecognition();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.hor-classes');
    if (section) {
      if (section._horResizeObserver) {
        section._horResizeObserver.disconnect();
        delete section._horResizeObserver;
      }
      section.removeAttribute('data-hor-initialized');
      initHallOfRecognition(section);
    }
  });
})();
